import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  NotificationFooter,
  NotificationHeader,
  NotificationItems,
  NotificationLink,
  NotificationListItem,
  NotificationMenu,
  ListItemData,
  NoNotification,
} from './styles/NotificationListModal.styled';
import NotificationTypeIcon from './NotificationTypeIcon';
import { NotificationService } from '@/services';
import { Notification } from '@/types';
import { AppDispatch, RootState } from '@/stores';
import { markAsRead, setNotificationCount } from '@/stores/notificationSlice';
import { getFormattedDateTime } from '@/utils/helpers';
import { useAuth } from '@/hooks/useAuth';
import { resolveNotificationTarget } from '@/utils/notificationTarget';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: (
    event: {},
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => void;
}

const NotificationListModal: React.FC<Props> = ({ anchorEl, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const unreadCount = useSelector((state: RootState) => state.notifications.count);
  const user = useAuth();
  const open = Boolean(anchorEl);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  useEffect(() => {
    if (!open) return;

    NotificationService.getList({ page: 1, limit: 10 })
      .then((res) => {
        const payload = res?.data?.data ? res.data : (res?.data ?? res);
        setNotifications(payload?.data ?? []);
        setTotal(payload?.total ?? 0);
      })
      .catch((error) => {
        console.error('Modal notifications load error:', error);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleItemClick = (note: Notification) => {
    if (!note.isRead) {
      dispatch(markAsRead(note.id));
    }

    onClose({}, 'backdropClick');
    const target = resolveNotificationTarget(note, user?.userType, user?.memberType);
    navigate(target.to, { state: target.state });
  };

  const handleViewAll = () => {
    onClose({}, 'backdropClick');
    navigate('/notifications');
  };

  const handleMarkAllRead = async () => {
    if (markingAllRead || unreadCount === 0) return;

    setMarkingAllRead(true);
    try {
      await NotificationService.markAllAsRead();
      dispatch(setNotificationCount(0));
      setNotifications((current) => current.map((note) => ({ ...note, isRead: 1 })));
    } catch (error) {
      console.error('Mark all as read failed:', error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  return (
    <NotificationMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        list: {
          'aria-label': 'Notifications',
        },
        paper: {
          elevation: 0,
          sx: {
            maxWidth: 'calc(100vw - 24px)',
            mt: 1.5,
            overflow: 'hidden',
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <NotificationHeader>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2">Notifications</Typography>
          <Typography component="p">
            {unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up'}
          </Typography>
        </Box>
        <Button
          aria-label="Mark all as read"
          disabled={markingAllRead || unreadCount === 0}
          onClick={handleMarkAllRead}
          size="small"
          startIcon={
            markingAllRead
              ? <CircularProgress color="inherit" size={14} />
              : <DoneAllRoundedIcon />
          }
        >
          Mark all read
        </Button>
      </NotificationHeader>

      <NotificationItems aria-label="Recent notifications">
        {notifications.map((note) => {
          const title = note.title ?? 'Notification';
          const message = note.message ?? '';

          return (
            <NotificationListItem key={note.id}>
              <NotificationLink
                className={note.isRead ? 'read' : ''}
                onClick={() => handleItemClick(note)}
              >
                <NotificationTypeIcon notification={note} size="small" />
                <ListItemData>
                  <Box className="notification-title-row">
                    <Typography variant="h6">{title}</Typography>
                    {!note.isRead && <Box className="unread-dot" aria-label="Unread" />}
                  </Box>
                  <Typography>{message}</Typography>
                  <Typography component="span">
                    {getFormattedDateTime(
                      typeof note.createdAt === 'string' ? new Date(note.createdAt) : note.createdAt
                    )}
                  </Typography>
                </ListItemData>
              </NotificationLink>
            </NotificationListItem>
          );
        })}
      </NotificationItems>

      {loading && notifications.length === 0 && (
        <NoNotification>
          <CircularProgress size={24} />
          <Typography component="span">Loading notifications</Typography>
        </NoNotification>
      )}

      {!loading && notifications.length === 0 && (
        <NoNotification>
          <NotificationTypeIcon
            notification={{ id: 0, isRead: 1, createdAt: new Date() }}
          />
          <Typography component="strong">No notifications yet</Typography>
          <Typography component="span">New activity will appear here.</Typography>
        </NoNotification>
      )}

      {notifications.length > 0 && (
        <NotificationFooter>
          <Button
            aria-label="View all notifications"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={handleViewAll}
          >
            View all notifications{total > notifications.length ? ` (${total})` : ''}
          </Button>
        </NotificationFooter>
      )}
    </NotificationMenu>
  );
};

export default NotificationListModal;
